import {
  ArrowRightFromLine,
  CreditCard,
  Download,
  Edit2,
  Eye,
  List,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api from "../services/api";

const ParticipantsList = ({ user, API_BASE_URL, getAuthHeader, logout }) => {
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [addParticipants, setAddParticipants] = useState([]);
  const [filteredAddParticipants, setFilteredAddParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  // Check screen width
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchParticipants = async () => {
    setRefreshing(true);
    try {
      const data = await api.registration.getAll();
      setParticipants(data);
      setFilteredParticipants(data);
    } catch (error) {
      console.error("Error fetching participants:", error);
      showNotification(error.message || "Failed to load participants", "error");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const fetchAddParticipants = async () => {
    setRefreshing(true);
    try {
      const data = await api.registration.getAllAddRegistrations();
      setAddParticipants(data);
      setFilteredAddParticipants(data);
    } catch (error) {
      console.error("Error fetching additional participants:", error);
      showNotification(error.message || "Failed to load additional participants", "error");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
    fetchAddParticipants();
  }, []);

  // Function to find matching additional participant
  const findMatchingAddParticipant = (mobile) => {
    return addParticipants.find(addP => addP.mobile === mobile);
  };

  // Apply search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredParticipants(participants);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      const filtered = participants.filter((p) =>
        Object.values({
          name: p.name,
          batch: p.batch,
          mobile: p.mobile,
          email: p.email,
          orderId: p.orderId,
          transactionId: p.transactionId,
          paymentStatus: p.paymentStatus,
        })
          .join(" ")
          .toLowerCase()
          .includes(lowerSearch)
      );
      setFilteredParticipants(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, participants]);

  const completedPayments = participants.filter(
    (p) => p.paymentStatus === "Success" || p.paymentStatus === "Paid"
  ).length;

  const showNotification = (message, type = "info") => {
    const existingNotification = document.querySelector(".custom-notification");
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement("div");
    notification.className = `custom-notification alert alert-${
      type === "error" ? "danger" : type === "success" ? "success" : "info"
    }`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      min-width: 300px;
      max-width: 500px;
      padding: 15px;
      border-radius: 5px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = `
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>${
            type === "error"
              ? "❌ Error"
              : type === "success"
              ? "✅ Success"
              : "ℹ️ Info"
          }</strong>
          <div class="mt-1">${message}</div>
        </div>
        <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
          ×
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  };

  const downloadExcel = () => {
    // Use all filtered participants, not just paginated ones
    const ws = XLSX.utils.json_to_sheet(
      filteredParticipants.map((p) => {
        const matchingAdd = findMatchingAddParticipant(p.mobile);
        const totalWithAdd = matchingAdd 
          ? p.totalAmount + matchingAdd.totalAmount 
          : p.totalAmount;
        
        // Use matchingAdd's accompany if available, otherwise use main registration's
        const spouse = matchingAdd ? (matchingAdd.accompany?.spouse || 0) : (p.accompany?.spouse || 0);
        const guest = matchingAdd ? (matchingAdd.accompany?.guest || 0) : (p.accompany?.guest || 0);
        const driver = matchingAdd ? (matchingAdd.accompany?.driver || 0) : (p.accompany?.driver || 0);
        const childAbove5 = matchingAdd ? (matchingAdd.accompany?.childAbove5 || 0) : (p.accompany?.childAbove5 || 0);
        const childBelow5 = matchingAdd ? (matchingAdd.accompany?.childBelow5 || 0) : (p.accompany?.childBelow5 || 0);
        
        // Use matchingAdd's child T-shirt sizes if available, otherwise use main registration's
        const childAbove5TshirtSize = matchingAdd 
          ? (matchingAdd.childAbove5TshirtSize || "N/A") 
          : (p.childAbove5TshirtSize || "N/A");
        const childBelow5TshirtSize = matchingAdd 
          ? (matchingAdd.childBelow5TshirtSize || "N/A") 
          : (p.childBelow5TshirtSize || "N/A");
        
        return {
          ID: p.id,
          Name: p.name,
          Batch: p.batch,
          Mobile: p.mobile,
          Email: p.email,
          "T-Shirt Size": p.tshirtSize,
          "Spouse": spouse,
          "Guest": guest,
          "Driver": driver,
          "Children Above 5": childAbove5,
          "Children Below 5": childBelow5,
          "Child Above 5 T-Shirt Size": childAbove5TshirtSize,
          "Child Below 5 T-Shirt Size": childBelow5TshirtSize,
          "Total Amount (Main)": p.totalAmount,
          "Total Amount (Additional)": matchingAdd?.totalAmount || 0,
          "Total Amount (Combined)": totalWithAdd,
          "Registration Date": p.registrationDate,
          "Order ID": p.orderId || "N/A",
          "Transaction ID": p.transactionId || "N/A",
          "Bank Transaction ID": p.bankTransactionId || "N/A",
          "Payment Status": p.paymentStatus,
          "Payment Gateway Status": p.paymentGatewayStatus || "N/A",
          "Payment Method": p.paymentMethod || "N/A",
          "Data Source": matchingAdd ? "Additional Registration" : "Main Registration",
          "Has Additional Payment": matchingAdd ? "Yes" : "No",
        };
      })
    );
    
    // Adjust column widths
    const wscols = [
      { wch: 5 },  // ID
      { wch: 20 }, // Name
      { wch: 15 }, // Batch
      { wch: 15 }, // Mobile
      { wch: 25 }, // Email
      { wch: 12 }, // T-Shirt Size
      { wch: 8 },  // Spouse
      { wch: 8 },  // Guest
      { wch: 8 },  // Driver
      { wch: 12 }, // Children Above 5
      { wch: 12 }, // Children Below 5
      { wch: 18 }, // Child Above 5 T-Shirt Size
      { wch: 18 }, // Child Below 5 T-Shirt Size
      { wch: 15 }, // Total Amount (Main)
      { wch: 15 }, // Total Amount (Additional)
      { wch: 15 }, // Total Amount (Combined)
      { wch: 15 }, // Registration Date
      { wch: 20 }, // Order ID
      { wch: 20 }, // Transaction ID
      { wch: 20 }, // Bank Transaction ID
      { wch: 15 }, // Payment Status
      { wch: 20 }, // Payment Gateway Status
      { wch: 15 }, // Payment Method
      { wch: 20 }, // Data Source
      { wch: 10 }, // Has Additional Payment
    ];
    
    ws['!cols'] = wscols;
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Participants");
    
    // Add filename with current date and total count
    const fileName = `participants_${new Date().toISOString().split("T")[0]}_total_${filteredParticipants.length}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    
    // Show notification with count
    showNotification(`Exported ${filteredParticipants.length} participants to Excel`, "success");
  };
  
  const handleView = (id) => {
    navigate(`/registration-details/${id}`);
  };

  const handleUpdatePayment = (mobile) => {
    navigate(`/update-participant/${mobile}`);
  };

  const handleEdit = (id) => {
    navigate(`/edit-registration/${id}`);
  };

  const handleDetails = async (mobile) => {
    try {
      // Search directory by mobile number
      const response = await api.directory.searchByMobile(mobile);

      // Check if response is an array and has items
      if (response && Array.isArray(response) && response.length > 0) {
        // Get the first matching participant
        const participant = response[0];
        navigate(`/directory-details/${participant.id}`);
      } else {
        showNotification("Participant not found in directory", "error");
      }
    } catch (error) {
      console.error("Error searching directory:", error);
      showNotification(
        error.message || "Failed to find participant in directory",
        "error"
      );
    }
  };

  const handleBasicDetailsDownload = async (mobile) => {
    try {
      // Search directory by mobile number
      const response = await api.directory.searchByMobile(mobile);

      // Check if response is an array and has items
      if (response && Array.isArray(response) && response.length > 0) {
        // Get the first matching participant
        const participant = response[0];
        navigate(`/directory-basic-details/${participant.id}`);
      } else {
        showNotification("Participant not found in directory", "error");
      }
    } catch (error) {
      console.error("Error searching directory:", error);
      showNotification(
        error.message || "Failed to find participant in directory",
        "error"
      );
    }
  };

  const handleSave = () => {
    navigate("/create-participant");
  };

  const handleAddPayment = () => {
    navigate("/add-payment-list");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this participant?"))
      return;

    try {
      const headers = getAuthHeader();
      headers["Content-Type"] = "application/json";

      const response = await fetch(`${API_BASE_URL}/api/registrations/${id}`, {
        method: "DELETE",
        headers: headers,
      });

      if (response.status === 401) {
        logout();
        navigate("/login");
        return;
      }

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to delete");

      setParticipants(participants.filter((p) => p.id !== id));
      showNotification("Participant deleted successfully", "success");
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(
        "Failed to delete participant: " + error.message,
        "error"
      );
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedParticipants = filteredParticipants.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const getParticipantsTableHeaders = () => {
    if (isMobile) {
      return [
        { key: "participant", label: "Participant", className: "col-6" },
        { key: "actions", label: "Actions", className: "col-6" },
      ];
    } else if (isTablet) {
      return [
        { key: "participant", label: "Participant", className: "col-3" },
        { key: "payment", label: "Payment", className: "col-3" },
        { key: "amount", label: "Amount", className: "col-2" },
        { key: "actions", label: "Actions", className: "col-4" },
      ];
    }
    return [
      { key: "id", label: "Id", className: "" },
      { key: "participant", label: "Participant", className: "" },
      { key: "batch", label: "Batch", className: "" },
      { key: "contact", label: "Contact", className: "" },
      { key: "payment", label: "Payment Status", className: "" },
      { key: "transaction", label: "Transaction Info", className: "" },
      { key: "method", label: "Payment Method", className: "" },
      { key: "amount", label: "Amount", className: "" },
      { key: "date", label: "Date", className: "" },
      { key: "actions", label: "Actions", className: "" },
    ];
  };

  const MobileParticipantsRow = ({ participant }) => {
    const matchingAdd = findMatchingAddParticipant(participant.mobile);
    const totalAmount = matchingAdd 
      ? participant.totalAmount + matchingAdd.totalAmount 
      : participant.totalAmount;

    return (
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-8">
              <h6
                className="mb-1"
                style={{ cursor: "pointer" }}
                onClick={() => handleDetails(participant.mobile)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleDetails(participant.mobile);
                  }
                }}
              >
                {participant.name}
                {matchingAdd && (
                  <span className="badge bg-info ms-2" style={{ fontSize: "10px" }}>+Add</span>
                )}
              </h6>
              <span className="badge bg-primary mb-2">{participant.batch}</span>
              <div className="small">
                <div>{participant.mobile}</div>
                <div className="text-truncate" style={{ maxWidth: "200px" }}>
                  {participant.email}
                </div>
                <div>{participant.id}</div>
                <div className="mt-2">
                  <span
                    className={`badge ${
                      participant.paymentStatus === "Success" ||
                      participant.paymentStatus === "Paid"
                        ? "bg-success"
                        : participant.paymentStatus === "Cancelled" ||
                          participant.paymentStatus === "Failed" ||
                          participant.paymentStatus === "Refunded"
                        ? "bg-danger"
                        : "bg-warning"
                    }`}
                  >
                    {participant.paymentStatus === "Success" ||
                    participant.paymentStatus === "Paid"
                      ? "Success"
                      : participant.paymentStatus === "Cancelled"
                      ? "Cancelled"
                      : participant.paymentStatus === "Failed"
                      ? "Failed"
                      : participant.paymentStatus === "Refunded"
                      ? "Refunded"
                      : "Pending"}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex flex-column gap-2">
                <div className="fw-bold">
                  ৳{totalAmount.toLocaleString()}
                  {matchingAdd && (
                    <small className="text-success d-block" style={{ fontSize: "10px" }}>
                      (Main: ৳{participant.totalAmount.toLocaleString()}<br />
                      Add: ৳{matchingAdd.totalAmount.toLocaleString()})
                    </small>
                  )}
                </div>
                <div className="d-flex flex-column gap-1">
                  {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                    <button
                      onClick={() => handleDelete(participant.id)}
                      className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}

                  {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                    <button
                      onClick={() => handleEdit(participant.id)}
                      className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => handleView(participant.id)}
                    className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                    title="View"
                  >
                    <Eye size={14} />
                  </button>
                  {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                    <button
                      onClick={() =>
                        handleBasicDetailsDownload(participant.mobile)
                      }
                      className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
                      title="Details"
                    >
                      <ArrowRightFromLine size={14} />
                    </button>
                  )}
                  {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                    <button
                      onClick={() => handleUpdatePayment(participant.mobile)}
                      className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center"
                      title="Update Payment"
                    >
                      <CreditCard size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TabletParticipantsRow = ({ participant }) => {
    const matchingAdd = findMatchingAddParticipant(participant.mobile);
    const totalAmount = matchingAdd 
      ? participant.totalAmount + matchingAdd.totalAmount 
      : participant.totalAmount;

    return (
      <tr>
        <td>
          <div
            style={{ cursor: "pointer" }}
            onClick={() => handleDetails(participant.mobile)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleDetails(participant.mobile);
              }
            }}
          >
            <strong>{participant.name}</strong>
            {matchingAdd && (
              <span className="badge bg-info ms-2" style={{ fontSize: "10px" }}>+Add</span>
            )}
            <div className="small">{participant.email}</div>
            <span className="badge bg-primary">{participant.batch}</span>
            <div>{participant.id}</div>
          </div>
        </td>
        <td>
          <span
            className={`badge ${
              participant.paymentStatus === "Success" ||
              participant.paymentStatus === "Paid"
                ? "bg-success"
                : participant.paymentStatus === "Cancelled" ||
                  participant.paymentStatus === "Failed" ||
                  participant.paymentStatus === "Refunded"
                ? "bg-danger"
                : "bg-warning"
            }`}
          >
            {participant.paymentStatus === "Success" ||
            participant.paymentStatus === "Paid"
              ? "Success"
              : participant.paymentStatus === "Cancelled"
              ? "Cancelled"
              : participant.paymentStatus === "Failed"
              ? "Failed"
              : participant.paymentStatus === "Refunded"
              ? "Refunded"
              : "Pending"}
          </span>
          <div className="small mt-1">{participant.paymentMethod || "N/A"}</div>
        </td>
        <td className="fw-bold">
          ৳{totalAmount.toLocaleString()}
          {matchingAdd && (
            <small className="text-success d-block" style={{ fontSize: "10px" }}>
              Main: ৳{participant.totalAmount.toLocaleString()}<br />
              Add: ৳{matchingAdd.totalAmount.toLocaleString()}
            </small>
          )}
        </td>
        <td>
          <div className="d-flex gap-1 flex-wrap">
            <button
              onClick={() => handleView(participant.id)}
              className="btn btn-sm btn-outline-primary"
              title="View"
            >
              <Eye size={14} />
            </button>
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleEdit(participant.id)}
                className="btn btn-sm btn-outline-warning"
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
            )}
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleDelete(participant.id)}
                className="btn btn-sm btn-outline-danger"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            )}
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleBasicDetailsDownload(participant.mobile)}
                className="btn btn-sm btn-outline-warning"
                title="Details"
              >
                <ArrowRightFromLine size={14} />
              </button>
            )}
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleUpdatePayment(participant.mobile)}
                className="btn btn-sm btn-outline-success"
                title="Update Payment"
              >
                <CreditCard size={14} />
              </button>
            )}
            <div className="small">
              {(() => {
                const date = new Date(participant.registrationDate);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
              })()}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (loading && participants.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading participants...</p>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        {/* Header with Actions */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h2 className="mb-0">Participant List</h2>
          <div className="d-flex flex-wrap gap-2">
            <div className="d-flex align-items-center">
              <span className="badge bg-primary me-2 py-3">
                Total Paid: ৳
                {
                  // Sum from main participants
                  (
                    participants
                      .filter(
                        (p) =>
                          p.paymentStatus === "Success" ||
                          p.paymentStatus === "Paid"
                      )
                      .reduce((sum, p) => sum + (p.totalAmount || 0), 0) +
                    // Sum from additional participants
                    addParticipants
                      .filter(
                        (p) =>
                          p.paymentStatus === "Success" ||
                          p.paymentStatus === "Paid"
                      )
                      .reduce((sum, p) => sum + (p.totalAmount || 0), 0)
                  ).toLocaleString()
                }
              </span>

              <span className="badge bg-success me-2 py-3">
                Paid: {completedPayments}
              </span>

              <span className="badge bg-info py-3">
                Total: {participants.length}
              </span>
              <span className="badge bg-warning ms-2 py-3">
                Add Payments: {addParticipants.length}
              </span>
            </div>
            <button
              className="btn btn-success d-flex align-items-center gap-2"
              onClick={downloadExcel}
            >
              <Download size={18} />
              <span className="d-none d-sm-inline">Export</span>
            </button>
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleSave()}
                className="btn btn-warning d-flex align-items-center gap-2"
                title="Create Participant"
              >
                <Save size={18} />

                <span className="d-none d-sm-inline">Create</span>
              </button>
            )}
            <button
              onClick={() => handleAddPayment()}
              className="btn btn-primary d-flex align-items-center gap-2"
              title="Additional Payment List"
            >
              <List size={18} />

              <span className="d-none d-sm-inline">Additional Payment</span>
            </button>
            <button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={fetchParticipants}
              disabled={refreshing}
            >
              <RefreshCw size={18} className={refreshing ? "spin" : ""} />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-3">
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search participants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm("")}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Controls - Top */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Show:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="small text-muted">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, filteredParticipants.length)} of{" "}
              {filteredParticipants.length} participants
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          </div>
        </div>

        {/* Participants Content */}
        {paginatedParticipants.length > 0 ? (
          <>
            {/* Mobile View */}
            {isMobile ? (
              <div>
                {paginatedParticipants.map((participant) => (
                  <MobileParticipantsRow
                    key={participant.id}
                    participant={participant}
                  />
                ))}
              </div>
            ) : /* Tablet View */
            isTablet ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      {getParticipantsTableHeaders().map((header) => (
                        <th key={header.key} className={header.className}>
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParticipants.map((participant) => (
                      <TabletParticipantsRow
                        key={participant.id}
                        participant={participant}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Desktop View */
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      {getParticipantsTableHeaders().map((header) => (
                        <th key={header.key}>{header.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParticipants.map((p) => {
                      const matchingAdd = findMatchingAddParticipant(p.mobile);
                      const totalAmount = matchingAdd 
                        ? p.totalAmount + matchingAdd.totalAmount 
                        : p.totalAmount;

                      return (
                        <tr key={p.id}>
                          <td>
                            <div>{p.id}</div>
                          </td>
                          <td>
                            <div
                              className="fw-bold"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDetails(p.mobile)}
                              role="button"
                              tabIndex={0}
                              onKeyPress={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  handleDetails(p.mobile);
                                }
                              }}
                            >
                              {p.name}
                              {matchingAdd && (
                                <span className="badge bg-info ms-2" style={{ fontSize: "10px" }}>+Add</span>
                              )}
                            </div>
                            <div className="small text-muted">{p.email}</div>
                          </td>
                          <td>
                            <span className="badge bg-primary">{p.batch}</span>
                          </td>
                          <td>
                            <div>{p.mobile}</div>
                            <div className="small text-muted">
                              T-Shirt: {p.tshirtSize}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                p.paymentStatus === "Success" ||
                                p.paymentStatus === "Paid"
                                  ? "bg-success"
                                  : p.paymentStatus === "Cancelled" ||
                                    p.paymentStatus === "Failed" ||
                                    p.paymentStatus === "Refunded"
                                  ? "bg-danger"
                                  : "bg-warning"
                              }`}
                            >
                              {p.paymentStatus === "Success" ||
                              p.paymentStatus === "Paid"
                                ? "Success"
                                : p.paymentStatus === "Cancelled"
                                ? "Cancelled"
                                : p.paymentStatus === "Failed"
                                ? "Failed"
                                : p.paymentStatus === "Refunded"
                                ? "Refunded"
                                : "Pending"}
                            </span>
                          </td>
                          <td>
                            <div className="small">
                              <div>
                                <strong>Order:</strong> {p.orderId || "N/A"}
                              </div>
                              <div>
                                <strong>Txn:</strong> {p.transactionId || "N/A"}
                              </div>
                              <div>
                                <strong>Bank:</strong>{" "}
                                {p.bankTransactionId || "N/A"}
                              </div>
                            </div>
                          </td>
                          <td>{p.paymentMethod || "N/A"}</td>
                          <td className="fw-bold">
                            ৳{totalAmount.toLocaleString()}
                            {matchingAdd && (
                              <small className="text-success d-block" style={{ fontSize: "10px" }}>
                                Main: ৳{p.totalAmount.toLocaleString()}<br />
                                Add: ৳{matchingAdd.totalAmount.toLocaleString()}
                              </small>
                            )}
                          </td>
                          <td>
                            <div className="small">
                              {(() => {
                                const date = new Date(p.registrationDate);
                                const day = String(date.getDate()).padStart(2, "0");
                                const month = String(date.getMonth() + 1).padStart(2, "0");
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                              })()}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {(user?.role === "ADMIN" ||
                                user?.role === "EDITOR") && (
                                <button
                                  onClick={() => handleDelete(p.id)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => handleView(p.id)}
                                className="btn btn-sm btn-outline-primary"
                                title="View"
                              >
                                <Eye size={16} />
                              </button>
                              {(user?.role === "ADMIN" ||
                                user?.role === "EDITOR") && (
                                <button
                                  onClick={() => handleEdit(p.id)}
                                  className="btn btn-sm btn-outline-warning"
                                  title="Edit"
                                >
                                  <Edit2 size={16} />
                                </button>
                              )}

                              {(user?.role === "ADMIN" ||
                                user?.role === "EDITOR") && (
                                <button
                                  onClick={() =>
                                    handleBasicDetailsDownload(p.mobile)
                                  }
                                  className="btn btn-sm btn-outline-warning"
                                  title="Details"
                                >
                                  <ArrowRightFromLine size={16} />
                                </button>
                              )}

                              {(user?.role === "ADMIN" ||
                                user?.role === "EDITOR") && (
                                <button
                                  onClick={() => handleUpdatePayment(p.mobile)}
                                  className="btn btn-sm btn-outline-success"
                                  title="Update Payment"
                                >
                                  <CreditCard size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls - Bottom */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="small text-muted">
                  Page {currentPage} of {totalPages}
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <li
                          key={pageNumber}
                          className={`page-item ${
                            currentPage === pageNumber ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      );
                    })}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="bi bi-person display-6 d-block mb-2"></i>
              No participants found
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantsList;

// import {
//   ArrowRightFromLine,
//   CreditCard,
//   Download,
//   Edit2,
//   Eye,
//   List,
//   RefreshCw,
//   Save,
//   Search,
//   Trash2,
//   X,
// } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import * as XLSX from "xlsx";
// import api from "../services/api";

// const ParticipantsList = ({ user, API_BASE_URL, getAuthHeader, logout }) => {
//   const [participants, setParticipants] = useState([]);
//   const [filteredParticipants, setFilteredParticipants] = useState([]);
//   const [addParticipants, setAddParticipants] = useState([]);
//   const [filteredAddParticipants, setFilteredAddParticipants] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);

//   // Pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage, setItemsPerPage] = useState(10);

//   const navigate = useNavigate();

//   // Check screen width
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
//   const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);

//   useEffect(() => {
//     const handleResize = () => {
//       setIsMobile(window.innerWidth < 768);
//       setIsTablet(window.innerWidth < 1024);
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const fetchParticipants = async () => {
//     setRefreshing(true);
//     try {
//       const data = await api.registration.getAll();
//       setParticipants(data);
//       setFilteredParticipants(data);
//     } catch (error) {
//       console.error("Error fetching participants:", error);
//       showNotification(error.message || "Failed to load participants", "error");
//     } finally {
//       setRefreshing(false);
//       setLoading(false);
//     }
//   };

//   const fetchAddParticipants = async () => {
//     setRefreshing(true);
//     try {
//       const data = await api.registration.getAllAddRegistrations();
//       setAddParticipants(data);
//       setFilteredAddParticipants(data);
//     } catch (error) {
//       console.error("Error fetching participants:", error);
//       showNotification(error.message || "Failed to load participants", "error");
//     } finally {
//       setRefreshing(false);
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchParticipants();
//     fetchAddParticipants();
//   }, []);

//   // Apply search filter
//   useEffect(() => {
//     if (!searchTerm.trim()) {
//       setFilteredParticipants(participants);
//     } else {
//       const lowerSearch = searchTerm.toLowerCase();
//       const filtered = participants.filter((p) =>
//         Object.values({
//           name: p.name,
//           batch: p.batch,
//           mobile: p.mobile,
//           email: p.email,
//           orderId: p.orderId,
//           transactionId: p.transactionId,
//           paymentStatus: p.paymentStatus,
//         })
//           .join(" ")
//           .toLowerCase()
//           .includes(lowerSearch)
//       );
//       setFilteredParticipants(filtered);
//     }
//     setCurrentPage(1); // Reset to first page when search changes
//   }, [searchTerm, participants]);

//   const completedPayments = participants.filter(
//     (p) => p.paymentStatus === "Success" || p.paymentStatus === "Paid"
//   ).length;

//   const showNotification = (message, type = "info") => {
//     const existingNotification = document.querySelector(".custom-notification");
//     if (existingNotification) {
//       existingNotification.remove();
//     }

//     const notification = document.createElement("div");
//     notification.className = `custom-notification alert alert-${
//       type === "error" ? "danger" : type === "success" ? "success" : "info"
//     }`;
//     notification.style.cssText = `
//       position: fixed;
//       top: 20px;
//       right: 20px;
//       z-index: 9999;
//       min-width: 300px;
//       max-width: 500px;
//       padding: 15px;
//       border-radius: 5px;
//       box-shadow: 0 4px 12px rgba(0,0,0,0.15);
//       animation: slideIn 0.3s ease-out;
//     `;

//     notification.innerHTML = `
//       <div class="d-flex justify-content-between align-items-start">
//         <div>
//           <strong>${
//             type === "error"
//               ? "❌ Error"
//               : type === "success"
//               ? "✅ Success"
//               : "ℹ️ Info"
//           }</strong>
//           <div class="mt-1">${message}</div>
//         </div>
//         <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
//           ×
//         </button>
//       </div>
//     `;

//     document.body.appendChild(notification);

//     setTimeout(() => {
//       if (notification.parentElement) {
//         notification.remove();
//       }
//     }, 5000);
//   };

//   // const downloadExcel = () => {
//   //   const ws = XLSX.utils.json_to_sheet(
//   //     paginatedParticipants.map((p) => ({
//   //       ID: p.id,
//   //       Name: p.name,
//   //       Batch: p.batch,
//   //       Mobile: p.mobile,
//   //       Email: p.email,
//   //       "T-Shirt Size": p.tshirtSize,
//   //       Adults: p.accompany.adult,
//   //       "Children (10+)": p.accompany.childAbove10,
//   //       "Children (10-)": p.accompany.childBelow10,
//   //       "Total Amount": p.totalAmount,
//   //       "Registration Date": p.registrationDate,
//   //       "Order ID": p.orderId,
//   //       "Transaction ID": p.transactionId,
//   //       "Bank Transaction ID": p.bankTransactionId,
//   //       "Payment Status": p.paymentStatus,
//   //       "Payment Gateway Status": p.paymentGatewayStatus,
//   //       "Payment Method": p.paymentMethod,
//   //     }))
//   //   );
//   //   const wb = XLSX.utils.book_new();
//   //   XLSX.utils.book_append_sheet(wb, ws, "Participants");
//   //   XLSX.writeFile(
//   //     wb,
//   //     `participants_${new Date().toISOString().split("T")[0]}.xlsx`
//   //   );
//   // };

//   const downloadExcel = () => {
//     const ws = XLSX.utils.json_to_sheet(
//       paginatedParticipants.map((p) => ({
//         ID: p.id,
//         Name: p.name,
//         Batch: p.batch,
//         Mobile: p.mobile,
//         Email: p.email,
//         "T-Shirt Size": p.tshirtSize,
//         "Spouse": p.accompany.spouse || 0,
//         "Guest": p.accompany.guest || 0,
//         "Driver": p.accompany.driver || 0,
//         "Children Above 5": p.accompany.childAbove5 || 0,
//         "Children Below 5": p.accompany.childBelow5 || 0,
//         "Child Above 5 T-Shirt Size": p.childAbove5TshirtSize || "N/A",
//         "Child Below 5 T-Shirt Size": p.childBelow5TshirtSize || "N/A",
//         "Total Amount": p.totalAmount,
//         "Registration Date": p.registrationDate,
//         "Order ID": p.orderId || "N/A",
//         "Transaction ID": p.transactionId || "N/A",
//         "Bank Transaction ID": p.bankTransactionId || "N/A",
//         "Payment Status": p.paymentStatus,
//         "Payment Gateway Status": p.paymentGatewayStatus || "N/A",
//         "Payment Method": p.paymentMethod || "N/A",
//       }))
//     );
    
//     // Adjust column widths
//     const wscols = [
//       { wch: 5 },  // ID
//       { wch: 20 }, // Name
//       { wch: 15 }, // Batch
//       { wch: 15 }, // Mobile
//       { wch: 25 }, // Email
//       { wch: 12 }, // T-Shirt Size
//       { wch: 8 },  // Spouse
//       { wch: 8 },  // Guest
//       { wch: 8 },  // Driver
//       { wch: 12 }, // Children Above 5
//       { wch: 12 }, // Children Below 5
//       { wch: 18 }, // Child Above 5 T-Shirt Size
//       { wch: 18 }, // Child Below 5 T-Shirt Size
//       { wch: 12 }, // Total Amount
//       { wch: 15 }, // Registration Date
//       { wch: 20 }, // Order ID
//       { wch: 20 }, // Transaction ID
//       { wch: 20 }, // Bank Transaction ID
//       { wch: 15 }, // Payment Status
//       { wch: 20 }, // Payment Gateway Status
//       { wch: 15 }, // Payment Method
//     ];
    
//     ws['!cols'] = wscols;
    
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Participants");
//     XLSX.writeFile(
//       wb,
//       `participants_${new Date().toISOString().split("T")[0]}.xlsx`
//     );
//   };
  
//   const handleView = (id) => {
//     navigate(`/registration-details/${id}`);
//   };

//   const handleUpdatePayment = (mobile) => {
//     navigate(`/update-participant/${mobile}`);
//   };

//   const handleEdit = (id) => {
//     navigate(`/edit-registration/${id}`);
//   };

//   const handleDetails = async (mobile) => {
//     try {
//       // Search directory by mobile number
//       const response = await api.directory.searchByMobile(mobile);

//       // Check if response is an array and has items
//       if (response && Array.isArray(response) && response.length > 0) {
//         // Get the first matching participant
//         const participant = response[0];
//         navigate(`/directory-details/${participant.id}`);
//       } else {
//         showNotification("Participant not found in directory", "error");
//       }
//     } catch (error) {
//       console.error("Error searching directory:", error);
//       showNotification(
//         error.message || "Failed to find participant in directory",
//         "error"
//       );
//     }
//   };

//   const handleBasicDetailsDownload = async (mobile) => {
//     try {
//       // Search directory by mobile number
//       const response = await api.directory.searchByMobile(mobile);

//       // Check if response is an array and has items
//       if (response && Array.isArray(response) && response.length > 0) {
//         // Get the first matching participant
//         const participant = response[0];
//         navigate(`/directory-basic-details/${participant.id}`);
//       } else {
//         showNotification("Participant not found in directory", "error");
//       }
//     } catch (error) {
//       console.error("Error searching directory:", error);
//       showNotification(
//         error.message || "Failed to find participant in directory",
//         "error"
//       );
//     }
//   };

//   const handleSave = () => {
//     navigate("/create-participant");
//   };

//   const handleAddPayment = () => {
//     navigate("/add-payment-list");
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this participant?"))
//       return;

//     try {
//       const headers = getAuthHeader();
//       headers["Content-Type"] = "application/json";

//       const response = await fetch(`${API_BASE_URL}/api/registrations/${id}`, {
//         method: "DELETE",
//         headers: headers,
//       });

//       if (response.status === 401) {
//         logout();
//         navigate("/login");
//         return;
//       }

//       const result = await response.json();
//       if (!response.ok) throw new Error(result.message || "Failed to delete");

//       setParticipants(participants.filter((p) => p.id !== id));
//       showNotification("Participant deleted successfully", "success");
//     } catch (error) {
//       console.error("Delete failed:", error);
//       showNotification(
//         "Failed to delete participant: " + error.message,
//         "error"
//       );
//     }
//   };

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const paginatedParticipants = filteredParticipants.slice(
//     indexOfFirstItem,
//     indexOfLastItem
//   );
//   const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   };

//   const handleItemsPerPageChange = (e) => {
//     setItemsPerPage(parseInt(e.target.value));
//     setCurrentPage(1);
//   };

//   const getParticipantsTableHeaders = () => {
//     if (isMobile) {
//       return [
//         { key: "participant", label: "Participant", className: "col-6" },
//         { key: "actions", label: "Actions", className: "col-6" },
//       ];
//     } else if (isTablet) {
//       return [
//         { key: "participant", label: "Participant", className: "col-3" },
//         { key: "payment", label: "Payment", className: "col-3" },
//         { key: "amount", label: "Amount", className: "col-2" },
//         { key: "actions", label: "Actions", className: "col-4" },
//       ];
//     }
//     return [
//       { key: "participant", label: "Participant", className: "" },
//       { key: "batch", label: "Batch", className: "" },
//       { key: "contact", label: "Contact", className: "" },
//       { key: "payment", label: "Payment Status", className: "" },
//       { key: "transaction", label: "Transaction Info", className: "" },
//       { key: "method", label: "Payment Method", className: "" },
//       { key: "amount", label: "Amount", className: "" },
//       { key: "date", label: "Date", className: "" },
//       { key: "actions", label: "Actions", className: "" },
//     ];
//   };

//   const MobileParticipantsRow = ({ participant }) => (
//     <div className="card mb-3">
//       <div className="card-body">
//         <div className="row">
//           <div className="col-8">
//             <h6
//               className="mb-1"
//               style={{ cursor: "pointer" }}
//               onClick={() => handleDetails(participant.mobile)}
//               role="button"
//               tabIndex={0}
//               onKeyPress={(e) => {
//                 if (e.key === "Enter" || e.key === " ") {
//                   handleDetails(participant.mobile);
//                 }
//               }}
//             >
//               {participant.name}
//             </h6>
//             <span className="badge bg-primary mb-2">{participant.batch}</span>
//             <div className="small">
//               <div>{participant.mobile}</div>
//               <div className="text-truncate" style={{ maxWidth: "200px" }}>
//                 {participant.email}
//               </div>
//               <div className="mt-2">
//                 <span
//                   className={`badge ${
//                     participant.paymentStatus === "Success" ||
//                     participant.paymentStatus === "Paid"
//                       ? "bg-success"
//                       : participant.paymentStatus === "Cancelled" ||
//                         participant.paymentStatus === "Failed" ||
//                         participant.paymentStatus === "Refunded"
//                       ? "bg-danger"
//                       : "bg-warning"
//                   }`}
//                 >
//                   {participant.paymentStatus === "Success" ||
//                   participant.paymentStatus === "Paid"
//                     ? "Success"
//                     : participant.paymentStatus === "Cancelled"
//                     ? "Cancelled"
//                     : participant.paymentStatus === "Failed"
//                     ? "Failed"
//                     : participant.paymentStatus === "Refunded"
//                     ? "Refunded"
//                     : "Pending"}
//                 </span>
//               </div>
//             </div>
//           </div>
//           <div className="col-4">
//             <div className="d-flex flex-column gap-2">
//               <div className="fw-bold">
//                 ৳{participant.totalAmount.toLocaleString()}
//               </div>
//               <div className="d-flex flex-column gap-1">
//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() => handleDelete(participant.id)}
//                     className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
//                     title="Delete"
//                   >
//                     <Trash2 size={14} />
//                   </button>
//                 )}

//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() => handleEdit(participant.id)}
//                     className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
//                     title="Edit"
//                   >
//                     <Edit2 size={14} />
//                   </button>
//                 )}
//                 <button
//                   onClick={() => handleView(participant.id)}
//                   className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
//                   title="View"
//                 >
//                   <Eye size={14} />
//                 </button>
//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() =>
//                       handleBasicDetailsDownload(participant.mobile)
//                     }
//                     className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
//                     title="Details"
//                   >
//                     <ArrowRightFromLine size={14} />
//                   </button>
//                 )}
//                 {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//                   <button
//                     onClick={() => handleUpdatePayment(participant.mobile)}
//                     className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center"
//                     title="Update Payment"
//                   >
//                     <CreditCard size={14} />
//                   </button>
//                 )}

//                 {/* <button
//                   onClick={() => handleView(participant.id)}
//                   className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
//                   title="View"
//                 >
//                   <Eye size={14} />
//                 </button> */}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const TabletParticipantsRow = ({ participant }) => (
//     <tr>
//       <td>
//         <div
//           style={{ cursor: "pointer" }}
//           onClick={() => handleDetails(participant.mobile)}
//           role="button"
//           tabIndex={0}
//           onKeyPress={(e) => {
//             if (e.key === "Enter" || e.key === " ") {
//               handleDetails(participant.mobile);
//             }
//           }}
//         >
//           <strong>{participant.name}</strong>
//           <div className="small">{participant.email}</div>
//           <span className="badge bg-primary">{participant.batch}</span>
//         </div>
//       </td>
//       <td>
//         <span
//           className={`badge ${
//             participant.paymentStatus === "Success" ||
//             participant.paymentStatus === "Paid"
//               ? "bg-success"
//               : participant.paymentStatus === "Cancelled" ||
//                 participant.paymentStatus === "Failed" ||
//                 participant.paymentStatus === "Refunded"
//               ? "bg-danger"
//               : "bg-warning"
//           }`}
//         >
//           {participant.paymentStatus === "Success" ||
//           participant.paymentStatus === "Paid"
//             ? "Success"
//             : participant.paymentStatus === "Cancelled"
//             ? "Cancelled"
//             : participant.paymentStatus === "Failed"
//             ? "Failed"
//             : participant.paymentStatus === "Refunded"
//             ? "Refunded"
//             : "Pending"}
//         </span>
//         <div className="small mt-1">{participant.paymentMethod || "N/A"}</div>
//       </td>
//       <td className="fw-bold">৳{participant.totalAmount.toLocaleString()}</td>
//       <td>
//         <div className="d-flex gap-1 flex-wrap">
//           <button
//             onClick={() => handleView(participant.id)}
//             className="btn btn-sm btn-outline-primary"
//             title="View"
//           >
//             <Eye size={14} />
//           </button>
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleEdit(participant.id)}
//               className="btn btn-sm btn-outline-warning"
//               title="Edit"
//             >
//               <Edit2 size={14} />
//             </button>
//           )}
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleDelete(participant.id)}
//               className="btn btn-sm btn-outline-danger"
//               title="Delete"
//             >
//               <Trash2 size={14} />
//             </button>
//           )}
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleBasicDetailsDownload(participant.mobile)}
//               className="btn btn-sm btn-outline-warning"
//               title="Details"
//             >
//               <ArrowRightFromLine size={14} />
//             </button>
//           )}
//           {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//             <button
//               onClick={() => handleUpdatePayment(participant.mobile)}
//               className="btn btn-sm btn-outline-success"
//               title="Update Payment"
//             >
//               <CreditCard size={14} />
//             </button>
//           )}
//           {/* <div className="small text-muted mt-1 w-100">
//             {new Date(participant.registrationDate).toLocaleDateString()}
//           </div> */}
//           <div className="small">
//             {(() => {
//               const date = new Date(participant.registrationDate);
//               const day = String(date.getDate()).padStart(2, "0");
//               const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
//               const year = date.getFullYear();
//               return `${day}/${month}/${year}`;
//             })()}
//           </div>
//         </div>
//       </td>
//     </tr>
//   );

//   if (loading && participants.length === 0) {
//     return (
//       <div className="text-center py-5">
//         <div className="spinner-border text-primary" role="status">
//           <span className="visually-hidden">Loading...</span>
//         </div>
//         <p className="mt-3">Loading participants...</p>
//       </div>
//     );
//   }

//   return (
//     <div className="card shadow-sm">
//       <div className="card-body">
//         {/* Header with Actions */}
//         <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
//           <h2 className="mb-0">Participant List</h2>
//           <div className="d-flex flex-wrap gap-2">
//             <div className="d-flex align-items-center">
//               {/* <span className="badge bg-primary me-2 py-3">
//                 Total Paid: ৳
//                 {participants
//                   .filter(
//                     (p) =>
//                       p.paymentStatus === "Success" ||
//                       p.paymentStatus === "Paid"
//                   )
//                   .reduce((sum, p) => sum + (p.totalAmount || 0), 0)
//                   .toLocaleString()}
//               </span> */}
//               {/* <span className="badge bg-success me-2 py-3">
//                 Paid: {completedPayments}
//               </span>
//               <span className="badge bg-info py-3">
//                 Total: {participants.length}
//               </span> */}
//               <span className="badge bg-primary me-2 py-3">
//                 Total Paid: ৳
//                 {
//                   // Sum from main participants
//                   (
//                     participants
//                       .filter(
//                         (p) =>
//                           p.paymentStatus === "Success" ||
//                           p.paymentStatus === "Paid"
//                       )
//                       .reduce((sum, p) => sum + (p.totalAmount || 0), 0) +
//                     // Sum from additional participants
//                     addParticipants
//                       .filter(
//                         (p) =>
//                           p.paymentStatus === "Success" ||
//                           p.paymentStatus === "Paid"
//                       )
//                       .reduce((sum, p) => sum + (p.totalAmount || 0), 0)
//                   ).toLocaleString()
//                 }
//               </span>

//               <span className="badge bg-success me-2 py-3">
//                 Paid: {completedPayments}
//               </span>

//               <span className="badge bg-info py-3">
//                 Total: {participants.length}
//               </span>
//             </div>
//             <button
//               className="btn btn-success d-flex align-items-center gap-2"
//               onClick={downloadExcel}
//             >
//               <Download size={18} />
//               <span className="d-none d-sm-inline">Export</span>
//             </button>
//             {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
//               <button
//                 onClick={() => handleSave()}
//                 className="btn btn-warning d-flex align-items-center gap-2"
//                 title="Create Participant"
//               >
//                 <Save size={18} />

//                 <span className="d-none d-sm-inline">Create</span>
//               </button>
//             )}
//             <button
//               onClick={() => handleAddPayment()}
//               className="btn btn-primary d-flex align-items-center gap-2"
//               title="Additional Payment List"
//             >
//               <List size={18} />

//               <span className="d-none d-sm-inline">Additional Payment</span>
//             </button>
//             <button
//               className="btn btn-outline-primary d-flex align-items-center gap-2"
//               onClick={fetchParticipants}
//               disabled={refreshing}
//             >
//               <RefreshCw size={18} className={refreshing ? "spin" : ""} />
//               <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
//             </button>
//           </div>
//         </div>

//         {/* Search */}
//         <div className="mb-3">
//           <div className="row">
//             <div className="col-md-6">
//               <div className="input-group">
//                 <span className="input-group-text">
//                   <Search size={16} />
//                 </span>
//                 <input
//                   type="text"
//                   className="form-control"
//                   placeholder="Search participants..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//                 {searchTerm && (
//                   <button
//                     className="btn btn-outline-secondary"
//                     onClick={() => setSearchTerm("")}
//                   >
//                     <X size={16} />
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Pagination Controls - Top */}
//         <div className="d-flex justify-content-between align-items-center mb-3">
//           <div className="d-flex align-items-center gap-3">
//             <div className="d-flex align-items-center gap-2">
//               <span className="small text-muted">Show:</span>
//               <select
//                 className="form-select form-select-sm"
//                 style={{ width: "auto" }}
//                 value={itemsPerPage}
//                 onChange={handleItemsPerPageChange}
//               >
//                 <option value="10">10</option>
//                 <option value="25">25</option>
//                 <option value="50">50</option>
//                 <option value="100">100</option>
//               </select>
//               {/* <span className="small text-muted">entries</span> */}
//             </div>
//             <div className="small text-muted">
//               Showing {indexOfFirstItem + 1} to{" "}
//               {Math.min(indexOfLastItem, filteredParticipants.length)} of{" "}
//               {filteredParticipants.length} participants
//               {searchTerm && ` matching "${searchTerm}"`}
//             </div>
//           </div>
//         </div>

//         {/* Participants Content */}
//         {paginatedParticipants.length > 0 ? (
//           <>
//             {/* Mobile View */}
//             {isMobile ? (
//               <div>
//                 {paginatedParticipants.map((participant) => (
//                   <MobileParticipantsRow
//                     key={participant.id}
//                     participant={participant}
//                   />
//                 ))}
//               </div>
//             ) : /* Tablet View */
//             isTablet ? (
//               <div className="table-responsive">
//                 <table className="table table-hover">
//                   <thead className="table-light">
//                     <tr>
//                       {getParticipantsTableHeaders().map((header) => (
//                         <th key={header.key} className={header.className}>
//                           {header.label}
//                         </th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {paginatedParticipants.map((participant) => (
//                       <TabletParticipantsRow
//                         key={participant.id}
//                         participant={participant}
//                       />
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             ) : (
//               /* Desktop View */
//               <div className="table-responsive">
//                 <table className="table table-hover">
//                   <thead className="table-light">
//                     <tr>
//                       {getParticipantsTableHeaders().map((header) => (
//                         <th key={header.key}>{header.label}</th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {paginatedParticipants.map((p) => (
//                       <tr key={p.id}>
//                         <td>
//                           <div
//                             className="fw-bold"
//                             style={{ cursor: "pointer" }}
//                             onClick={() => handleDetails(p.mobile)}
//                             role="button"
//                             tabIndex={0}
//                             onKeyPress={(e) => {
//                               if (e.key === "Enter" || e.key === " ") {
//                                 handleDetails(p.mobile);
//                               }
//                             }}
//                           >
//                             {p.name}
//                           </div>
//                           <div className="small text-muted">{p.email}</div>
//                         </td>
//                         <td>
//                           <span className="badge bg-primary">{p.batch}</span>
//                         </td>
//                         <td>
//                           <div>{p.mobile}</div>
//                           <div className="small text-muted">
//                             T-Shirt: {p.tshirtSize}
//                           </div>
//                         </td>
//                         <td>
//                           <span
//                             className={`badge ${
//                               p.paymentStatus === "Success" ||
//                               p.paymentStatus === "Paid"
//                                 ? "bg-success"
//                                 : p.paymentStatus === "Cancelled" ||
//                                   p.paymentStatus === "Failed" ||
//                                   p.paymentStatus === "Refunded"
//                                 ? "bg-danger"
//                                 : "bg-warning"
//                             }`}
//                           >
//                             {p.paymentStatus === "Success" ||
//                             p.paymentStatus === "Paid"
//                               ? "Success"
//                               : p.paymentStatus === "Cancelled"
//                               ? "Cancelled"
//                               : p.paymentStatus === "Failed"
//                               ? "Failed"
//                               : p.paymentStatus === "Refunded"
//                               ? "Refunded"
//                               : "Pending"}
//                           </span>
//                         </td>
//                         <td>
//                           <div className="small">
//                             <div>
//                               <strong>Order:</strong> {p.orderId || "N/A"}
//                             </div>
//                             <div>
//                               <strong>Txn:</strong> {p.transactionId || "N/A"}
//                             </div>
//                             <div>
//                               <strong>Bank:</strong>{" "}
//                               {p.bankTransactionId || "N/A"}
//                             </div>
//                           </div>
//                         </td>
//                         <td>{p.paymentMethod || "N/A"}</td>
//                         <td className="fw-bold">
//                           ৳{p.totalAmount.toLocaleString()}
//                         </td>
//                         <td>
//                           {/* <div className="small">
//                             {new Date(p.registrationDate).toLocaleDateString()}
//                           </div> */}
//                           <div className="small">
//                             {(() => {
//                               const date = new Date(p.registrationDate);
//                               const day = String(date.getDate()).padStart(
//                                 2,
//                                 "0"
//                               );
//                               const month = String(
//                                 date.getMonth() + 1
//                               ).padStart(2, "0"); // Months are 0-based
//                               const year = date.getFullYear();
//                               return `${day}/${month}/${year}`;
//                             })()}
//                           </div>
//                         </td>
//                         <td>
//                           <div className="d-flex gap-1">
//                             {(user?.role === "ADMIN" ||
//                               user?.role === "EDITOR") && (
//                               <button
//                                 onClick={() => handleDelete(p.id)}
//                                 className="btn btn-sm btn-outline-danger"
//                                 title="Delete"
//                               >
//                                 <Trash2 size={16} />
//                               </button>
//                             )}
//                             <button
//                               onClick={() => handleView(p.id)}
//                               className="btn btn-sm btn-outline-primary"
//                               title="View"
//                             >
//                               <Eye size={16} />
//                             </button>
//                             {(user?.role === "ADMIN" ||
//                               user?.role === "EDITOR") && (
//                               <button
//                                 onClick={() => handleEdit(p.id)}
//                                 className="btn btn-sm btn-outline-warning"
//                                 title="Edit"
//                               >
//                                 <Edit2 size={16} />
//                               </button>
//                             )}

//                             {(user?.role === "ADMIN" ||
//                               user?.role === "EDITOR") && (
//                               <button
//                                 onClick={() =>
//                                   handleBasicDetailsDownload(p.mobile)
//                                 }
//                                 className="btn btn-sm btn-outline-warning"
//                                 title="Details"
//                               >
//                                 <ArrowRightFromLine size={16} />
//                               </button>
//                             )}

//                             {(user?.role === "ADMIN" ||
//                               user?.role === "EDITOR") && (
//                               <button
//                                 onClick={() => handleUpdatePayment(p.mobile)}
//                                 className="btn btn-sm btn-outline-success"
//                                 title="Update Payment"
//                               >
//                                 <CreditCard size={16} />
//                               </button>
//                             )}
//                           </div>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             )}

//             {/* Pagination Controls - Bottom */}
//             {totalPages > 1 && (
//               <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
//                 <div className="small text-muted">
//                   Page {currentPage} of {totalPages}
//                 </div>
//                 <nav>
//                   <ul className="pagination pagination-sm mb-0">
//                     <li
//                       className={`page-item ${
//                         currentPage === 1 ? "disabled" : ""
//                       }`}
//                     >
//                       <button
//                         className="page-link"
//                         onClick={() => handlePageChange(currentPage - 1)}
//                         disabled={currentPage === 1}
//                       >
//                         Previous
//                       </button>
//                     </li>

//                     {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
//                       let pageNumber;
//                       if (totalPages <= 5) {
//                         pageNumber = i + 1;
//                       } else if (currentPage <= 3) {
//                         pageNumber = i + 1;
//                       } else if (currentPage >= totalPages - 2) {
//                         pageNumber = totalPages - 4 + i;
//                       } else {
//                         pageNumber = currentPage - 2 + i;
//                       }

//                       return (
//                         <li
//                           key={pageNumber}
//                           className={`page-item ${
//                             currentPage === pageNumber ? "active" : ""
//                           }`}
//                         >
//                           <button
//                             className="page-link"
//                             onClick={() => handlePageChange(pageNumber)}
//                           >
//                             {pageNumber}
//                           </button>
//                         </li>
//                       );
//                     })}

//                     <li
//                       className={`page-item ${
//                         currentPage === totalPages ? "disabled" : ""
//                       }`}
//                     >
//                       <button
//                         className="page-link"
//                         onClick={() => handlePageChange(currentPage + 1)}
//                         disabled={currentPage === totalPages}
//                       >
//                         Next
//                       </button>
//                     </li>
//                   </ul>
//                 </nav>
//               </div>
//             )}
//           </>
//         ) : (
//           <div className="text-center py-5">
//             <div className="text-muted">
//               <i className="bi bi-person display-6 d-block mb-2"></i>
//               No participants found
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ParticipantsList;
